/**
 * GED Coffre-Fort — File API client functions (DOC-003).
 *
 * Wraps apiFetch for file-related GED endpoints (CRUD, upload, download,
 * references, quota, bulk, copy).
 */

import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { erreurDuCoffre } from '@/lib/api/ged-owned-by-tender'
import type {
    FileListFilters,
    FileListResponse,
    GedFile,
    GedPresignedUrl,
    GedQuota,
    UpdateFilePayload,
} from '@/hooks/useGed.types'

const GED_BASE = `${API_BASE}/v1/ged`

// ─── Headers helper ─────────────────────────────────────────────────

function entityHeaders(entityId: string): Record<string, string> {
    return { 'X-Entity-Id': entityId }
}

// ─── FILES ──────────────────────────────────────────────────────────

export async function fetchFiles(
    entityId: string,
    filters?: FileListFilters,
): Promise<FileListResponse> {
    const params = new URLSearchParams()
    if (filters?.view) params.set('view', filters.view)
    if (filters?.folder_id) params.set('folder_id', filters.folder_id)
    if (filters?.show_all) params.set('show_all', 'true')
    if (filters?.status) params.set('status', filters.status)
    if (filters?.search) params.set('search', filters.search)
    // DOC-003bis Phase 3 — filtres de regroupement de la vue « attached ».
    if (filters?.parent_type) params.set('parent_type', filters.parent_type)
    if (filters?.client_id) params.set('client_id', filters.client_id)
    if (filters?.instructor_id) params.set('instructor_id', filters.instructor_id)
    // ⚠️ QUAL-001 / RSC-003 — sans ces DEUX lignes, le filtre part du nœud
    // d'arbre, traverse le type, et se perd ICI : la requête n'en porte pas
    // trace et le serveur rend TOUT le coffre. Aucune erreur, un écran
    // simplement faux — c'est la marche que la sérialisation ligne à ligne
    // rate à chaque nouvelle zone.
    if (filters?.nonconformity_id) params.set('nonconformity_id', filters.nonconformity_id)
    if (filters?.resource_id) params.set('resource_id', filters.resource_id)
    // TND-008 — TROISIÈME occurrence du même défaut : le nœud d'UNE
    // consultation émettait `tender_id`, et la clé se perdait ici. Le clic
    // listait les documents de TOUTES les consultations.
    if (filters?.tender_id) params.set('tender_id', filters.tender_id)
    // CHK-015 §7.3 — QUATRIÈME occurrence évitée : une clé émise par
    // l'arbre et perdue ici ne lève rien, elle rend toute la zone.
    if (filters?.quote_id) params.set('quote_id', filters.quote_id)
    if (filters?.learner_id) params.set('learner_id', filters.learner_id)
    if (filters?.location_id) params.set('location_id', filters.location_id)
    if (filters?.parent_id) params.set('parent_id', filters.parent_id)
    if (filters?.file_id) params.set('file_id', filters.file_id)
    if (filters?.provenance) params.set('provenance', filters.provenance)
    if (filters?.type_filter) params.set('type', filters.type_filter)
    if (filters?.review_filter) params.set('review', filters.review_filter)
    // Héritage multi-entités : le coffre affiche AUSSI les documents des entités
    // parentes (marqués is_inherited, lecture seule, badge « Hérité »). Cohérent
    // avec l'export par scope qui les inclut déjà.
    params.set('inherit_mode', 'dynamic')
    if (filters?.cursor_created_at) params.set('cursor_created_at', filters.cursor_created_at)
    if (filters?.cursor_id) params.set('cursor_id', filters.cursor_id)
    if (filters?.limit) params.set('limit', String(filters.limit))

    const qs = params.toString()
    const res = await apiFetch(`${GED_BASE}/files${qs ? `?${qs}` : ''}`, {
        headers: entityHeaders(entityId),
    })
    if (!res.ok) await handleApiError(res)
    return res.json()
}

export async function fetchFileDetail(
    entityId: string,
    fileId: string,
): Promise<GedFile> {
    const res = await apiFetch(`${GED_BASE}/files/${fileId}`, {
        headers: entityHeaders(entityId),
    })
    if (!res.ok) await handleApiError(res)
    return res.json()
}

export async function uploadFile(
    entityId: string,
    file: File,
    folderId?: string | null,
): Promise<GedFile> {
    const formData = new FormData()
    formData.append('file', file)

    const url = folderId
        ? `${GED_BASE}/files/upload?folder_id=${encodeURIComponent(folderId)}`
        : `${GED_BASE}/files/upload`

    const res = await apiFetch(url, {
        method: 'POST',
        headers: entityHeaders(entityId),
        body: formData,
    })
    // 🔴 **H10, mesuré le 2026-09-20 — le QUATRIÈME chemin jumeau.** Cette porte
    // jetait un `Error` NU : ni `code`, ni `request_id`, ni `details`. Ses trois
    // sœurs les portent depuis le 2026-09-19 (`uploadFileWithProgress`,
    // `replaceFile`, `copyFile`), si bien que `useUploadFile` ne POUVAIT pas lire
    // les deux 413 de taille même en le voulant — un `err.code` toujours
    // `undefined` laisse la lecture parfaitement verte et parfaitement morte.
    // `'move'` en geste, comme `uploadFileWithProgress` : le refus qui arrive ici
    // est celui de la DESTINATION (A1, dépôt dans un dossier d'AO), dont la
    // phrase n'interpole aucun verbe.
    if (!res.ok) throw await erreurDuCoffre(res, 'move', 'Upload failed')
    return res.json()
}

export async function updateFile(
    entityId: string,
    fileId: string,
    data: UpdateFilePayload,
): Promise<GedFile> {
    const res = await apiFetch(`${GED_BASE}/files/${fileId}`, {
        method: 'PATCH',
        headers: { ...entityHeaders(entityId), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
    // ✅ 2026-09-18 — le PATCH ne refuse plus que « déplacer » (D1) : l'écran
    // déduisait « modifier » d'un corps sans `folder_id`, verbe que
    // `_geste_du_patch` n'émet plus. Le refus de la DESTINATION, lui, a sa
    // propre phrase et ne lit pas le geste.
    if (!res.ok) throw await erreurDuCoffre(res, 'move', 'Failed to update file')
    return res.json()
}

export async function replaceFile(
    entityId: string,
    fileId: string,
    file: File,
): Promise<GedFile> {
    const formData = new FormData()
    formData.append('file', file)

    const res = await apiFetch(`${GED_BASE}/files/${fileId}/replace`, {
        method: 'PUT',
        headers: entityHeaders(entityId),
        body: formData,
    })
    if (!res.ok) throw await erreurDuCoffre(res, 'replace', 'Replace failed')
    return res.json()
}

export async function deleteFile(
    entityId: string,
    fileId: string,
): Promise<{ deleted: boolean; id: string }> {
    const res = await apiFetch(`${GED_BASE}/files/${fileId}`, {
        method: 'DELETE',
        headers: entityHeaders(entityId),
    })
    if (!res.ok) throw await erreurDuCoffre(res, 'delete', 'Failed to delete file')
    return res.json()
}

// ─── DOWNLOAD / PREVIEW ────────────────────────────────────────────

export async function fetchDownloadUrl(
    entityId: string,
    fileId: string,
): Promise<GedPresignedUrl> {
    const res = await apiFetch(`${GED_BASE}/files/${fileId}/download-url`, {
        headers: entityHeaders(entityId),
    })
    // Le refus garde son code, son statut et sa référence (`ApiError`) : l'écran le dit
    // dans sa langue. `new Error('Failed to get download URL: 403')` jetait les trois — et
    // « Failed to fetch files: 403 » passait pour une COUPURE (`estUnEchecReseau`).
    if (!res.ok) await handleApiError(res)
    return res.json()
}

export async function fetchPreviewUrl(
    entityId: string,
    fileId: string,
): Promise<GedPresignedUrl> {
    const res = await apiFetch(`${GED_BASE}/files/${fileId}/preview-url`, {
        headers: entityHeaders(entityId),
    })
    if (!res.ok) await handleApiError(res)
    return res.json()
}

// ─── REFERENCES ─────────────────────────────────────────────────────

export interface FileReferences {
    file_id: string
    total: number
    references: { source: string; count: number }[]
}

export async function fetchFileReferences(
    entityId: string,
    fileId: string,
): Promise<FileReferences> {
    const res = await apiFetch(`${GED_BASE}/files/${fileId}/references`, {
        headers: entityHeaders(entityId),
    })
    if (!res.ok) await handleApiError(res)
    return res.json()
}

// ─── QUOTA ──────────────────────────────────────────────────────────

export async function fetchQuota(entityId: string): Promise<GedQuota> {
    const res = await apiFetch(`${GED_BASE}/quota`, {
        headers: entityHeaders(entityId),
    })
    if (!res.ok) await handleApiError(res)
    return res.json()
}

// ─── BULK ACTIONS ───────────────────────────────────────────────────

export async function bulkDeleteFiles(
    entityId: string,
    fileIds: string[],
): Promise<{ deleted_count: number }> {
    const res = await apiFetch(`${GED_BASE}/files/bulk-delete`, {
        method: 'POST',
        headers: { ...entityHeaders(entityId), 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_ids: fileIds }),
    })
    // 🔴 **E1, mesuré le 2026-09-19** — cette porte jetait
    // `new Error('Bulk delete failed: ' + statut)`, et `toastApiError` affiche
    // `err.message` tel quel (`lib/toast-error.ts` l.43) : l'utilisateur lisait
    // un statut HTTP, et le `request_id` d'une vraie panne partait avec. Même
    // défaut, même remède qu'`updateFolder` la veille.
    if (!res.ok) throw await erreurDuCoffre(res, 'delete', 'Bulk delete failed')
    return res.json()
}

export async function bulkMoveFiles(
    entityId: string,
    fileIds: string[],
    folderId: string | null,
): Promise<{ moved_count: number }> {
    const res = await apiFetch(`${GED_BASE}/files/bulk-move`, {
        method: 'POST',
        headers: { ...entityHeaders(entityId), 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_ids: fileIds, folder_id: folderId }),
    })
    // 🔴 **E1 — le refus de la DESTINATION se lit par son CODE.** Depuis A2
    // (2026-09-19, `api/ged_bulk.py` l.134), viser un dossier d'appel d'offres
    // rend 409 `GED_FOLDER_OWNED_BY_TENDER` ; l'écran affichait
    // « Bulk move failed: 409 ». `erreurDuCoffre` sert la phrase de la CIBLE
    // (`phraseDuRefusDUnDossierCible`), qui n'attendait qu'un appelant.
    // ⚠️ `'move'` est un repli de verbe : la phrase de la destination
    // n'interpole aucun geste (voir `ged-bulk-apercu.ts`).
    if (!res.ok) throw await erreurDuCoffre(res, 'move', 'Bulk move failed')
    return res.json()
}

// Les APERÇUS d'impact (`?dry_run=true`) vivent dans `ged-bulk-apercu.ts` —
// coupés d'ici le 2026-09-19 pour tenir le plafond de 300 lignes ; le motif du
// découpage est en tête de ce module.

// ─── COPY ───────────────────────────────────────────────────────────

export async function copyFile(
    entityId: string,
    fileId: string,
    targetFolderId?: string | null,
): Promise<GedFile> {
    const res = await apiFetch(`${GED_BASE}/files/${fileId}/copy`, {
        method: 'POST',
        headers: { ...entityHeaders(entityId), 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_folder_id: targetFolderId ?? null }),
    })
    // 🔴 **F6, mesuré le 2026-09-19 (soir) — la COPIE était la porte oubliée.**
    // Elle levait un `new Error` avec le message SERVEUR brut, alors que
    // `api/ged_copy.py` l.110 émet le 409 `GED_FOLDER_OWNED_BY_TENDER` depuis C9
    // (2026-09-18) : copier vers un dossier d'appel d'offres montrait donc une
    // phrase française dans l'interface anglaise, et le `request_id` d'une vraie
    // panne partait avec — `supportCodeOf` ne le lit que sur une `ApiError`.
    // ⚠️ `'move'` est un repli de verbe, comme au déplacement de masse : le refus
    // qui arrive ici est celui de la DESTINATION, dont la phrase n'interpole
    // aucun geste.
    if (!res.ok) throw await erreurDuCoffre(res, 'move', 'Copy failed')
    return res.json()
}
